import { BaseLogo, ProductPrice } from "../../core/models/entities/BaseLogo";
import { enviroment } from "../../enviroment";

// "Held price ladder" predicates (31/10; flags REACT_APP_LOGO_PRICE_HELD_ENABLED
// = A and REACT_APP_LOGO_PRICE_DETAIL_PATCH_ENABLED = B).
//
// The API (flag PriceCurrencyListHoldEnabled, js-kitmaker-api#1295) answers a
// LIST row whose serial price channel is still empty - while the stored
// supplier template rows carry a foreign currency for the viewer - with
// LogoDto.priceHeld = true and product.prices = [] instead of the EUR ladder
// (list rows only; GetLogo, the popup, the cart and the order never see it).
// Every UI decision for such a row - the badge in place of the blank price
// cell, the row's add-to-order lock, the list re-read - goes through
// isLogoPriceHeld, so with flag A off they are ALL false whatever the API
// sent: no element, no attribute, no call = byte-identical DOM and wire.
// Pure functions; no React.

export function isLogoPriceHeld(logo: BaseLogo | null | undefined): boolean {
  if (!enviroment.logoPriceHeldEnabled) return false;
  if (logo?.priceHeld !== true) return false;
  // Rows present = the hold is over (a detail patch or a list refetch landed
  // them), whatever the mark still says.
  return !hasPriceRows(logo);
}

export function hasPriceRows(logo: BaseLogo | null | undefined): boolean {
  const prices = logo?.product?.prices;
  return Array.isArray(prices) && prices.length > 0;
}

// Bulk "Add to Order (n)" (useLogoActionsHandler.handleAddItemsToCart): the
// part of a selection whose ladder is HELD goes to `skipped`, the rest to
// `orderable`, order kept - the API contract for a held list row ("must not
// add the row to the cart from the list while it is true": its prices are
// []), the same rule the row's own locked button follows. The caller names
// the count in ONE toast (LOGOS_SKIPPED_PRICE_HELD_KEY, the sibling of
// orderability's skip toasts). Flag A off = nothing skipped (the selection
// untouched); a row whose rows have landed is never skipped (isLogoPriceHeld).
export const LOGOS_SKIPPED_PRICE_HELD_KEY = "logoList.skippedPriceHeld";

export function splitPriceHeldLogos(logos: BaseLogo[]): {
  orderable: BaseLogo[];
  skipped: BaseLogo[];
} {
  const orderable: BaseLogo[] = [];
  const skipped: BaseLogo[] = [];
  for (const logo of logos) {
    (isLogoPriceHeld(logo) ? skipped : orderable).push(logo);
  }
  return { orderable, skipped };
}

// The currency rule every FE price reader applies (LogoPrices.tsx:
// `currency || "DKK"`) and the API's PriceCurrencyStrictPolicy.Normalize
// ("null/blank = DKK; otherwise trimmed and upper-cased"): a row WITHOUT a
// currency IS a DKK row - the API's price drain writes ''-stamped DKK rows,
// Newtonsoft may send null, and the F1 re-stamp to "DKK" only reaches
// viewers in the DKK org scope. Without this an EUR list row was never
// patched from a ''-stamped DKK detail (the popup read "DKK", the list kept
// EUR), and 'dkk' against 'DKK' counted as two currencies (a needless cache
// write).
export const DEFAULT_CURRENCY = "DKK";
export function normalizeCurrency(currency: string | null | undefined): string {
  const trimmed = typeof currency === "string" ? currency.trim() : "";
  return trimmed === "" ? DEFAULT_CURRENCY : trimmed.toUpperCase();
}

// The ONE (normalized) currency every row of a ladder is priced in;
// undefined for no rows or a mixed ladder (never guessed).
export function pricesCurrency(
  prices: readonly ProductPrice[] | null | undefined,
): string | undefined {
  if (!prices || prices.length === 0) return undefined;
  let currency: string | undefined;
  for (const row of prices) {
    const rowCurrency = normalizeCurrency(row?.currency);
    if (currency === undefined) currency = rowCurrency;
    else if (currency !== rowCurrency) return undefined;
  }
  return currency;
}

// Flag B's one rule for logoDetailCache.patchRowFromDetail: may this landed
// GetLogo answer (`detail`) replace the LIST row's ladder although neither
// side is pending? Yes when the row is HELD (the detail is the ladder the
// hold waited for) or when the row's rows are priced in ANOTHER currency
// than the detail's for the same logo (the stored EUR template rows against
// the viewer's own DKK ladder). The detail is the server's newest answer for
// that logo in the viewer's pricing scope, so this is only fresher data from
// the same source - never a conversion, never an amount computed here (FAST
// REGEL 18/10). A detail WITHOUT rows carries no information (a product the
// supplier has not priced yet) and never applies; the same (normalized)
// currency - blank / null / 'dkk' all read as DKK - or a mixed ladder on
// either side is today's rule (pending rows only). Flag off = false.
export function detailPricesSupersedeRow(row: BaseLogo, detail: BaseLogo): boolean {
  if (!enviroment.logoPriceDetailPatchEnabled) return false;
  if (!row || !detail || row.id !== detail.id) return false;
  if (!hasPriceRows(detail)) return false;
  if (row.priceHeld === true) return true;
  const rowCurrency = pricesCurrency(row.product?.prices);
  const freshCurrency = pricesCurrency(detail.product?.prices);
  return (
    rowCurrency !== undefined &&
    freshCurrency !== undefined &&
    rowCurrency !== freshCurrency
  );
}
